import axios from 'axios';
import { auth } from '../Firebase/firebase';
import * as Sentry from '@sentry/react-native';
import { sanitizeRequestData } from './sanitizeRequestData';
import { attachReplayableRequest } from './attachReplayableRequest';

// Get backend API URL from environment or use default
const API_BASE_URL = process.env.EXPO_PUBLIC_BACKEND_API_URL || 'http://localhost:3000/api';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds - /api/settings aggregates 14 data sources
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add Firebase auth token
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.warn('Failed to get Firebase auth token for API request:', error);
      // Continue without token - backend will reject if auth is required
    }
    // Strip non-serializable Firestore refs / cyclical structures from the body so axios's
    // internal JSON.stringify can't crash on iOS ("cyclical structure in JSON object",
    // Sentry QARASAY-KASSA-26A/26B). No-op for GET (no body) and opaque bodies (FormData).
    if (config.data !== undefined && config.data !== null) {
      config.data = sanitizeRequestData(config.data);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // ATL-1077: make the failure replayable BEFORE anything else can return or throw. Only
    // `@atlas/api-client` used to attach this, so every dual-write issued through this raw
    // instance — `organizations.create` at signup among them — was refused by the outbox's
    // `no-replayable-request` rule and lost with no retry anywhere.
    attachReplayableRequest(error);
    // ATL-2035: a caller that reports this failure itself (`readApi` in supabaseDataHelpers) says
    // so on the request. Reporting it here too made the SDK skip the caller's richer capture (its
    // already-captured check is by object identity), or — when the caller wrapped the error — sent
    // the same failure twice.
    if (error.config?.callerReportsFailure === true) {
      console.warn('API request failed (reported by caller):', error.message);
      return Promise.reject(error);
    }
    // Log errors but don't throw - we want Firebase writes to succeed even if API fails
    if (error.response) {
      console.warn('API request failed:', error.response.status, error.response.data);
      Sentry.captureException(error, {
        tags: {
          api_error: true,
          status: error.response.status,
        },
        extra: {
          url: error.config?.url,
          method: error.config?.method,
          data: error.response.data,
        },
      });
    } else if (error.request) {
      console.warn('API request failed - no response:', error.message);
      Sentry.captureException(error, {
        tags: {
          api_error: true,
          network_error: true,
        },
        extra: {
          url: error.config?.url,
          method: error.config?.method,
        },
      });
    } else {
      console.warn('API request error:', error.message);
    }
    // Return a rejected promise but don't throw - let caller handle it
    return Promise.reject(error);
  }
);

// Export the configured axios instance
export default apiClient;

// Export convenience methods
export const api = {
  get: (url, config) => apiClient.get(url, config),
  post: (url, data, config) => apiClient.post(url, data, config),
  put: (url, data, config) => apiClient.put(url, data, config),
  patch: (url, data, config) => apiClient.patch(url, data, config),
  delete: (url, config) => apiClient.delete(url, config),
};

